import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, locationsAPI, storesAPI, s3API, stripeConnectAPI, authAPI, State, City, Store, Listing } from '../api';
import { getCurrentLocation, findLocationIds } from '../utils/geolocation';
import { compressImage } from '../utils/imageCompression';
import SellerHoursEditor from '../components/SellerHoursEditor';
import { defaultSellerHours, parseSellerHoursFromApi, sellerHoursHasAny, sellerHoursPayloadIfAny, sellerHoursToJson, SellerHoursMap } from '../utils/sellerHours';
import { createSlug } from '../utils/slug';
import LocationAutocomplete from '../components/LocationAutocomplete';
import MapboxMapWrapper from '../components/MapboxMapWrapper';
import {
  LISTING_CATEGORY_KEYS,
  formatListingCategoryLabel,
  normalizeListingCategory,
  resolveListingCategory,
} from '../constants/listingCategories';
import { clearHomeListingsCache } from '../utils/homeScrollRestoration';

// Delivery Fee Tiers Editor Component
interface FeeTier {
  from: number | string;
  to: number | string; // Can be number or empty string for clearing
  fee: number | string;
}

function validateFeeTiersLive(feeTiers: FeeTier[], maxMiles: number): string | null {
  if (!feeTiers.length) return null;
  const maxMi = maxMiles > 0 ? maxMiles : 0;
  for (let i = 0; i < feeTiers.length; i++) {
    const tier = feeTiers[i];
    const from =
      typeof tier.from === 'string'
        ? tier.from === ''
          ? NaN
          : parseFloat(tier.from)
        : tier.from;
    const to =
      typeof tier.to === 'string' ? (tier.to === '' ? NaN : parseFloat(tier.to)) : tier.to;
    if (Number.isNaN(to) || tier.to === '') continue;
    if (!Number.isNaN(from) && to <= from) {
      return `Tier ${i + 1}: "To" (${to}) must be greater than "From" (${from})`;
    }
    if (maxMi > 0 && to > maxMi) {
      return `Tier ${i + 1}: "To" (${to} miles) cannot exceed maximum delivery miles (${maxMi} miles)`;
    }
    if (i > 0) {
      const prevTier = feeTiers[i - 1];
      const prevTo =
        typeof prevTier.to === 'string'
          ? prevTier.to === ''
            ? 0
            : parseFloat(prevTier.to) || 0
          : prevTier.to;
      if (!Number.isNaN(from) && from < prevTo) {
        return `Tier ${i + 1}: "From" (${from}) should be at least ${prevTo}`;
      }
    }
    if (i === 0 && !Number.isNaN(from) && from !== 0) {
      return 'First tier must start at 0 miles';
    }
  }
  const lastTier = feeTiers[feeTiers.length - 1];
  const lastTo =
    typeof lastTier.to === 'string'
      ? lastTier.to === ''
        ? NaN
        : parseFloat(lastTier.to)
      : lastTier.to;
  if (maxMi > 0 && !Number.isNaN(lastTo) && Math.abs(lastTo - maxMi) > 0.001) {
    return "Last tier's \"To\" must match maximum delivery miles";
  }
  return null;
}

const DeliveryFeeTiersEditor: React.FC<{
  tiers: string;
  onChange: (tiers: string) => void;
  freeMiles: number;
  maxMiles: number;
  onMaxMilesChange?: (maxMiles: number) => void;
  error?: string;
}> = ({ tiers, onChange, freeMiles, maxMiles, onMaxMilesChange, error }) => {
  const [feeTiers, setFeeTiers] = useState<FeeTier[]>(() => {
    try {
      const parsed = JSON.parse(tiers || '[]');
      // Convert any open-ended tiers (to === from or to === null) to specific values
      const initialTiers = parsed.map((tier: FeeTier, index: number) => {
        // If tier is open-ended (to === from or to === null), set to a specific value
        const fromNum = typeof tier.from === 'string' ? parseFloat(tier.from) || 0 : tier.from;
        const toNum = typeof tier.to === 'string' ? parseFloat(tier.to) || 0 : tier.to;
        if (tier.to === null || toNum === fromNum) {
          return { ...tier, to: fromNum + 5 }; // Default to 5 miles range
        }
        return tier;
      });
      // If we have tiers and maxMiles is set, update the last tier's "to" value
      if (initialTiers.length > 0 && maxMiles > 0) {
        initialTiers[initialTiers.length - 1].to = maxMiles;
      }
      return initialTiers;
    } catch {
      const defaultTiers = [{ from: 0, to: 5, fee: 0 }, { from: 5, to: 15, fee: 2.99 }];
      // If maxMiles is set, update the last tier
      if (maxMiles > 0 && maxMiles !== 15) {
        defaultTiers[defaultTiers.length - 1].to = maxMiles;
      }
      return defaultTiers;
    }
  });

  // Sync feeTiers with tiers prop when it changes externally
  const prevTiersRef = useRef(tiers);
  
  useEffect(() => {
    // Only sync if tiers prop changed externally (not from our own onChange)
    if (prevTiersRef.current !== tiers) {
      prevTiersRef.current = tiers;
      try {
        const parsed = JSON.parse(tiers || '[]');
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Normalize tiers: ensure continuity and last tier matches maxMiles
          const normalized = parsed.map((tier: FeeTier, index: number) => {
            if (index === 0) {
              // First tier should start at 0
              return { ...tier, from: 0 };
            } else {
              // Each tier should start where the previous one ends
              const prevTier = parsed[index - 1];
              const prevTo = typeof prevTier.to === 'string' ? parseFloat(prevTier.to) || 0 : prevTier.to;
              const tierFrom = typeof tier.from === 'string' ? parseFloat(tier.from) || 0 : tier.from;
              return { ...tier, from: Math.max(prevTo, tierFrom) };
            }
          });
          
          // Ensure last tier's "to" matches maxMiles
          if (normalized.length > 0 && maxMiles > 0) {
            const lastTier = normalized[normalized.length - 1];
            const lastFrom = typeof lastTier.from === 'string' ? parseFloat(lastTier.from) || 0 : lastTier.from;
            if (lastFrom < maxMiles) {
              normalized[normalized.length - 1] = { ...lastTier, to: maxMiles };
            }
          }
          
          // Only update if different to avoid unnecessary re-renders
          const currentStr = JSON.stringify(feeTiers);
          const newStr = JSON.stringify(normalized);
          if (currentStr !== newStr) {
            setFeeTiers(normalized);
          }
        }
      } catch {
        // Ignore parse errors
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiers]);

  // Update parent when tiers change
  useEffect(() => {
    prevTiersRef.current = JSON.stringify(feeTiers);
    onChange(JSON.stringify(feeTiers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeTiers]);

  const addTier = () => {
    const lastTier = feeTiers[feeTiers.length - 1];
    // Calculate where the new tier should start
    let newFrom: number;
    let newTo: number;
    
    if (!lastTier) {
      newFrom = 0;
      newTo = 5; // Default range of 5 miles
    } else {
      const lastTo = typeof lastTier.to === 'string' ? parseFloat(lastTier.to) || 0 : lastTier.to;
      const lastFrom = typeof lastTier.from === 'string' ? parseFloat(lastTier.from) || 0 : lastTier.from;
      newFrom = lastTo || lastFrom;
      // Default new tier to span 5 miles, or to maxMiles if that's closer
      newTo = Math.min(newFrom + 5, maxMiles || newFrom + 5);
      // If newTo would equal newFrom, add at least 1 more
      if (newTo <= newFrom) {
        newTo = newFrom + 1;
      }
    }
    // Add a new tier with a specific "to" value
    // Ensure to is always greater than from
    setFeeTiers([...feeTiers, { from: newFrom, to: Math.max(newTo, newFrom + 1), fee: 2.99 }]);
  };

  const updateTier = (index: number, field: keyof FeeTier, value: number | string) => {
    const updated = [...feeTiers];
    updated[index] = { ...updated[index], [field]: value };
    setFeeTiers(updated);
  };

  const liveTierError = useMemo(
    () => validateFeeTiersLive(feeTiers, maxMiles),
    [feeTiers, maxMiles],
  );
  const displayError = error?.trim() ? error : liveTierError;

  const removeTier = (index: number) => {
    if (feeTiers.length <= 1) return; // Don't allow removing the last tier
    
    const updated = feeTiers.filter((_, i) => i !== index);
    
    // If we removed a tier, we need to adjust the remaining tiers to ensure continuity
    // Adjust the "from" values of tiers after the removed one
    for (let i = index; i < updated.length; i++) {
      if (i === 0) {
        // First tier should start at 0
        updated[i].from = 0;
      } else {
        // Each tier should start where the previous one ends
        updated[i].from = updated[i - 1].to || updated[i - 1].from;
      }
    }
    
    setFeeTiers(updated);
  };

  return (
    <div className="space-y-3">
      {displayError ? (
        <p className="text-sm text-red-600" role="alert">
          {displayError}
        </p>
      ) : null}
      {feeTiers.map((tier, index) => {
        return (
          <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1 grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-gray-600">From (miles)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={tier.from}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateTier(index, 'from', val === '' ? '' : val);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">
                  To (miles) {index === feeTiers.length - 1 && <span className="text-green-600 font-medium">Max miles</span>}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={tier.to === '' ? '' : tier.to}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateTier(index, 'to', val === '' ? '' : val);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Fee ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={tier.fee === '' ? '' : tier.fee}
                  onChange={(e) => updateTier(index, 'fee', parseFloat(e.target.value) || 0)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>
            {feeTiers.length > 1 && (
              <button
                type="button"
                onClick={() => removeTier(index)}
                className="text-red-600 hover:text-red-800 px-2"
              >
                ×
              </button>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={addTier}
        className="text-sm text-green-600 hover:text-green-800 font-medium"
      >
        + Add Delivery Custom Fee
      </button>
      <p className="text-xs text-gray-500">
        Example: 0-5 miles = Free, 5-15 miles = $2.99, 15-20 miles = $5.99
      </p>
    </div>
  );
};

const CreateListing: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: 'general',
    state_id: '',
    city_id: '',
    quantity: '1',
    delivery_time_option: '1-2',
    returns_money_back: false,
    store_id: '',
    is_shop_store: false,
    shop_store_name: '',
    shop_store_address: '',
    shop_store_close_time: '',
    discount_type: '' as 'percentage' | 'fixed' | '',
    discount_value: '',
    discount_min_quantity: '2',
    delivery_fee_type: 'fixed' as 'distance' | 'fixed',
    shipping_fee: '2.99',
    max_delivery_miles: '25',
    free_delivery_miles: '5',
    delivery_fee_tiers: JSON.stringify([
      { from: 0, to: 5, fee: 0 },
      { from: 5, to: 25, fee: 2.99 }
    ]),
    condition: 'new' as 'new' | 'new_open_box' | 'seller_refurbished' | 'used' | 'for_parts',
  });
  const [itemType, setItemType] = useState<'food' | 'other' | ''>('');
  const [error, setError] = useState('');
  const [deliveryFeeError, setDeliveryFeeError] = useState('');
  const deliveryFeeErrorRef = useRef<HTMLDivElement>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [payoutInfoComplete, setPayoutInfoComplete] = useState(false);
  const [checkingRequirements, setCheckingRequirements] = useState(true);
  const pendingCityIdRef = useRef<number | null>(null);
  const pendingCityNameRef = useRef<string | null>(null);
  const [mapCoords, setMapCoords] = useState<[number, number] | null>(null);
  const [listingLocationAddress, setListingLocationAddress] = useState('');
  const [resolvedCityName, setResolvedCityName] = useState('');
  const [mapLoading, setMapLoading] = useState(false);
  const [showCustomMaxMiles, setShowCustomMaxMiles] = useState(true);
  const [sellerHours, setSellerHours] = useState<SellerHoursMap>(defaultSellerHours());
  const [sellerHoursError, setSellerHoursError] = useState<string | null>(null);
  const sellerHoursRef = useRef<HTMLDivElement>(null);

  const geocodeAddress = async (address: string) => {
    if (!address) return;
    
    setMapLoading(true);
    setMapCoords(null);
    
    try {
      // Add a small delay to respect Nominatim rate limits
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          headers: {
            'User-Agent': 'BuyDrop/1.0'
          }
        }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        // Validate coordinates
        if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
          setMapCoords([lat, lon]);
        } else {
          console.error('Invalid coordinates received:', lat, lon);
        }
      } else {
        console.log('No geocoding results for address:', address);
        setMapCoords(null);
      }
    } catch (err) {
      console.error('Error geocoding address:', err, 'Address:', address);
      setMapCoords(null);
    } finally {
      setMapLoading(false);
    }
  };

  const checkPayoutAndTaxRequirements = async () => {
    setCheckingRequirements(true);
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 10000)
      );
      
      const checkPromise = stripeConnectAPI.getAccountStatus().catch(() => ({ 
        connected: false, 
        details_submitted: false, 
        charges_enabled: false, 
        payouts_enabled: false 
      }));
      
      const accountStatus = await Promise.race([checkPromise, timeoutPromise]) as any;
      
      // Check Stripe Connect account status
      const hasPayoutInfo = accountStatus?.connected && 
                           accountStatus?.details_submitted && 
                           accountStatus?.charges_enabled && 
                           accountStatus?.payouts_enabled;
      setPayoutInfoComplete(hasPayoutInfo);
    } catch (err) {
      console.error('Error checking requirements:', err);
      // If there's an error, assume incomplete to be safe
      setPayoutInfoComplete(false);
    } finally {
      setCheckingRequirements(false);
    }
  };

  // Ensure category is valid when itemType changes
  useEffect(() => {
    if (itemType === 'food') {
      // If category is not 'food' or 'shop', reset to 'food'
      if (formData.category !== 'food' && formData.category !== 'shop') {
        setFormData(prev => ({ ...prev, category: 'food' }));
      }
    } else if (itemType === 'other') {
      // If category is 'food' or 'shop', reset to 'general'
      // Only reset if it's an invalid category for 'other'
      if (formData.category === 'food' || formData.category === 'shop') {
        setFormData(prev => ({ ...prev, category: 'general' }));
      }
      // Ensure category is not empty when itemType is 'other'
      if (!formData.category || formData.category === '') {
        setFormData(prev => ({ ...prev, category: 'general' }));
      }
    }
  }, [itemType]); // Only depend on itemType, not formData.category

  useEffect(() => {
    loadStates();
    loadStores();
    if (isEditMode && id) {
      loadListing();
      setCheckingRequirements(false);
      setPayoutInfoComplete(true);
    } else {
      loadSellerHoursFromProfile();
      checkPayoutAndTaxRequirements();
    }
  }, [id, isEditMode]);

  // Scroll to delivery fee error when it's set
  useEffect(() => {
    if (deliveryFeeError && deliveryFeeErrorRef.current) {
      setTimeout(() => {
        deliveryFeeErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [deliveryFeeError]);

  const loadListing = async () => {
    if (!id) return;
    try {
      const listing = await listingsAPI.getOne(parseInt(id));
      
      // Check if user owns the listing
      if (listing.user_id !== user?.id) {
        toast.error('You do not have permission to edit this listing');
        navigate('/my-listings');
        return;
      }
      
      const category = resolveListingCategory(listing.category);
      const isFoodCategory = category === 'food' || category === 'shop';
      
      // Load images - support both image_urls array and single image_url
      const images = listing.image_urls && Array.isArray(listing.image_urls) 
        ? listing.image_urls 
        : (listing.image_url ? [listing.image_url] : []);
      
      setUploadedImages(images);
      
      // Prefer address from listing upload (not store address)
      const savedAddress = listing.address || listing.shop_store_address || '';
      
      setFormData({
        title: listing.title || '',
        description: listing.description || '',
        price: listing.price ? listing.price.toString() : '',
        category: category,
        state_id: listing.state_id != null ? listing.state_id.toString() : '',
        city_id: listing.city_id != null ? listing.city_id.toString() : '',
        quantity: listing.quantity?.toString() || '1',
        delivery_time_option: listing.delivery_time_option || '1-2',
        returns_money_back: listing.returns_money_back === true || listing.returns_money_back === 1,
        store_id: listing.store_id ? listing.store_id.toString() : '',
        is_shop_store: listing.is_shop_store ? true : false,
        shop_store_name: listing.shop_store_name || '',
        shop_store_address: savedAddress,
        shop_store_close_time: listing.shop_store_close_time || '',
        discount_type: (listing.discount_type as 'percentage' | 'fixed' | '') || '',
        discount_value: listing.discount_value ? listing.discount_value.toString() : '',
        discount_min_quantity: listing.discount_min_quantity ? listing.discount_min_quantity.toString() : '2',
        delivery_fee_type: listing.max_delivery_miles || listing.delivery_fee_tiers ? 'distance' : 'fixed',
        shipping_fee: (listing.shipping_fee != null && listing.shipping_fee !== '') ? String(listing.shipping_fee) : '2.99',
        max_delivery_miles: listing.max_delivery_miles ? listing.max_delivery_miles.toString() : '',
        free_delivery_miles: listing.free_delivery_miles ? listing.free_delivery_miles.toString() : '5',
        delivery_fee_tiers: listing.delivery_fee_tiers || JSON.stringify([
          { from: 0, to: 5, fee: 0 },
          { from: 5, to: 15, fee: 2.99 }
        ]),
        condition: (listing.condition as 'new' | 'new_open_box' | 'seller_refurbished' | 'used' | 'for_parts') || 'new',
      });
      
      // Set location address for map display (saved address when editing)
      const savedLocation = listing.city_name || listing.state_name || listing.state_abbreviation
        ? [listing.city_name, listing.state_abbreviation || listing.state_name].filter(Boolean).join(', ')
        : '';
      setResolvedCityName(listing.city_name || '');
      if (savedAddress) {
        setListingLocationAddress(savedAddress);
        geocodeAddress(savedAddress);
      } else if (savedLocation) {
        setListingLocationAddress(savedLocation);
        geocodeAddress(savedLocation);
      }

      if (!listing.store_id) {
        const hoursRaw =
          listing.seller_hours ??
          (listing.shop_store_close_time?.trim().startsWith('{')
            ? listing.shop_store_close_time
            : null);
        setSellerHours(
          parseSellerHoursFromApi(
            hoursRaw,
            listing.seller_open_time,
            listing.seller_close_time,
          ),
        );
      }

      // Set item type based on category
      setItemType(isFoodCategory ? 'food' : 'other');
      // Load cities for the state
      await loadCities(listing.state_id);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to load listing');
      navigate('/my-listings');
    }
  };

  const loadStores = async () => {
    try {
      const data = await storesAPI.getMyStores();
      setStores(data);
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  };

  const loadSellerHoursFromProfile = async () => {
    try {
      const data = await authAPI.getMe();
      const user = data?.user;
      if (
        user &&
        (user.seller_hours || user.seller_open_time || user.seller_close_time)
      ) {
        setSellerHours(
          parseSellerHoursFromApi(user.seller_hours, user.seller_open_time, user.seller_close_time),
        );
      }
    } catch (err) {
      console.error('Error loading seller hours:', err);
    }
  };

  const detectLocation = async () => {
    setLocationLoading(true);
    try {
      const location = await getCurrentLocation();
      
      if (location.stateAbbreviation) {
        const { stateId, cityId } = await findLocationIds(
          location.city || '',
          location.stateAbbreviation
        );
        
        if (stateId != null) {
          setFormData(prev => ({ ...prev, state_id: stateId.toString() }));
          // Load cities for the state
          const citiesData = await locationsAPI.getCitiesByState(stateId);
          setCities(citiesData);
          
          if (cityId != null) {
            setFormData(prev => ({ ...prev, city_id: cityId.toString() }));
          }
        }
      }
    } catch (error) {
      console.log('Location detection failed or denied:', error);
      // User denied location or error occurred - that's okay
    } finally {
      setLocationLoading(false);
    }
  };

  useEffect(() => {
    if (formData.state_id) {
      const stateIdNum = parseInt(formData.state_id);
      if (!isNaN(stateIdNum)) {
        console.log('Loading cities for state_id:', stateIdNum);
        loadCities(stateIdNum).then(() => {
          console.log('Cities loaded, pending city info:', {
            cityId: pendingCityIdRef.current,
            cityName: pendingCityNameRef.current
          });
        });
      }
    } else {
      setCities([]);
      pendingCityIdRef.current = null;
      pendingCityNameRef.current = null;
    }
  }, [formData.state_id]);

  // Separate effect to match city after cities are loaded
  useEffect(() => {
    // Only proceed if we have cities loaded and a state selected
    if (cities.length > 0 && formData.state_id) {
      // Try to set the city if we have a pending city ID or name
      if (pendingCityIdRef.current !== null && pendingCityIdRef.current !== undefined) {
        // We have a city ID, use it directly
        console.log('Setting city_id from pending ID:', pendingCityIdRef.current);
        const cityId = pendingCityIdRef.current.toString();
        // Only update if city_id is different or empty
        if (formData.city_id !== cityId) {
          setFormData(prev => ({ ...prev, city_id: cityId }));
        }
        pendingCityIdRef.current = null;
        pendingCityNameRef.current = null;
      } else if (pendingCityNameRef.current !== null && pendingCityNameRef.current !== undefined) {
        // We have a city name but no ID, try to find it in the loaded cities
        const cityName = pendingCityNameRef.current.toLowerCase().trim();
        console.log('Trying to match city name:', cityName, 'in cities:', cities.map((c: City) => c.name));
        
        // Try exact match first
        let matchedCity = cities.find((c: City) => {
          const dbCityName = c.name.toLowerCase().trim();
          return dbCityName === cityName;
        });
        
        // If no exact match, try fuzzy matching
        if (!matchedCity) {
          matchedCity = cities.find((c: City) => {
            const dbCityName = c.name.toLowerCase().trim();
            // Try multiple matching strategies
            return dbCityName.includes(cityName) ||
                   cityName.includes(dbCityName) ||
                   // Handle common variations like "San Francisco" vs "San Francisco City"
                   (cityName.replace(/\s+/g, ' ') === dbCityName.replace(/\s+/g, ' ')) ||
                   // Handle cases where one has extra words - check if all words from cityName are in dbCityName
                   (cityName.split(' ').length > 0 && cityName.split(' ').every(word => word.length > 2 && dbCityName.includes(word))) ||
                   // Or vice versa
                   (dbCityName.split(' ').length > 0 && dbCityName.split(' ').every(word => word.length > 2 && cityName.includes(word)));
          });
        }
        
        if (matchedCity && matchedCity.id != null) {
          console.log('Found city match:', matchedCity.name, matchedCity.id);
          const cityId = matchedCity.id.toString();
          // Only update if city_id is different or empty
          if (formData.city_id !== cityId) {
            setFormData(prev => ({ ...prev, city_id: cityId }));
          }
        } else {
          console.log('City not found in database:', pendingCityNameRef.current);
          console.log('Available cities:', cities.map((c: City) => c.name).slice(0, 20));
          
          // Try to add the city to the database
          if (pendingCityNameRef.current && formData.state_id) {
            const cityNameToAdd = pendingCityNameRef.current;
            const stateIdStr = formData.state_id;
            
            // Use async function inside useEffect
            (async () => {
              try {
                const stateIdNum = parseInt(stateIdStr);
                if (!isNaN(stateIdNum)) {
                  console.log('Attempting to add city to database:', cityNameToAdd, 'state_id:', stateIdNum);
                  const newCity = await locationsAPI.addCity(cityNameToAdd, stateIdNum);
                  console.log('City added to database:', newCity);
                  
                  // Reload cities to include the new one
                  const updatedCities = await locationsAPI.getCitiesByState(stateIdNum);
                  setCities(updatedCities);
                  
                  // Set the city_id to the newly created city
                  if (newCity && newCity.id) {
                    setFormData(prev => ({ ...prev, city_id: newCity.id.toString() }));
                    toast.success(`Added "${cityNameToAdd}" to the database`);
                  }
                }
              } catch (addCityErr: any) {
                console.error('Error adding city to database:', addCityErr);
                toast.error(`Could not add city "${cityNameToAdd}". Please select a city from the dropdown.`);
              }
            })();
          }
        }
        pendingCityNameRef.current = null;
      }
    }
  }, [cities, formData.state_id, formData.city_id]);

  const loadStates = async () => {
    try {
      const data = await locationsAPI.getStates();
      setStates(data);
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadCities = async (stateId: number) => {
    try {
      const data = await locationsAPI.getCitiesByState(stateId);
      setCities(data);
      return data; // Return the cities so we can use them in the useEffect
    } catch (error) {
      console.error('Error loading cities:', error);
      return [];
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Limit to 10 images
    const filesToUpload = Array.from(files).slice(0, 10 - uploadedImages.length);
    if (files.length > filesToUpload.length) {
      toast.error(`You can only upload up to 10 images. Only the first ${filesToUpload.length} will be uploaded.`);
    }

    setUploadingImages(true);
    setError('');

    try {
      // Compress images before upload
      const compressedFiles = await Promise.all(
        filesToUpload.map(async (file) => {
          // If it's an image file, compress it
          if (file.type.startsWith('image/')) {
            try {
              // Convert file to data URL for compression
              const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
              });
              
              // Compress the image (max 1920x1920, 80% quality)
              const compressedFile = await compressImage(dataUrl, 1920, 1920, 0.8);
              return compressedFile;
            } catch (err) {
              console.warn('Failed to compress image, using original:', err);
              return file; // Fallback to original if compression fails
            }
          }
          return file;
        })
      );

      // Get signed URLs for all files
      const filesData = compressedFiles.map(file => ({
        fileName: file.name,
        fileType: file.type
      }));

      const { uploadUrls } = await s3API.getUploadUrls(filesData);

      // Upload each file to S3
      const uploadPromises = uploadUrls.map(async (urlData: any, index: number) => {
        const file = compressedFiles[index];
        const response = await fetch(urlData.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        return urlData.fileUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setUploadedImages([...uploadedImages, ...uploadedUrls]);
    } catch (err: any) {
      console.error('Error uploading images:', err);
      setError(err.response?.data?.error || 'Failed to upload images. Please try again.');
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages(uploadedImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    console.log('=== FORM SUBMISSION START ===');
    console.log('Form state:', {
      title: formData.title,
      category: normalizeListingCategory(formData.category),
      city_id: formData.city_id,
      state_id: formData.state_id,
      itemType,
      payoutInfoComplete,
      isEditMode,
      checkingRequirements
    });

    // Check requirements for new listings only
    if (!isEditMode) {
      if (!payoutInfoComplete) {
        const errorMsg = `Please complete your payout information (Stripe Connect account setup) before posting a listing. You can do this in Settings.`;
        console.log('Requirements check failed:', { payoutInfoComplete });
        setError(errorMsg);
        setLoading(false);
        return;
      }
    }

    if (!itemType) {
      setError('Choose food or items using the toggle at the top');
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const errors: Record<string, boolean> = {};
    const errorMessages: string[] = [];

    // Validate Title
    if (!formData.title || formData.title.trim() === '') {
      errors.title = true;
      errorMessages.push('Title is required');
    } else if (formData.title.trim().length < 3) {
      errors.title = true;
      errorMessages.push('Title must be at least 3 characters long');
    }

    // Validate Description
    if (!formData.description || formData.description.trim() === '') {
      errors.description = true;
      errorMessages.push('Description is required');
    } else if (formData.description.trim().length < 10) {
      errors.description = true;
      errorMessages.push('Description must be at least 10 characters long');
    }

    // Validate Price
    if (!formData.price || formData.price.trim() === '') {
      errors.price = true;
      errorMessages.push('Price is required');
    } else {
      const priceValue = parseFloat(formData.price);
      if (isNaN(priceValue) || priceValue <= 0) {
        errors.price = true;
        errorMessages.push('Price must be a valid number greater than 0');
      }
    }

    // Validate Category
    if (!formData.category || formData.category.trim() === '') {
      errors.category = true;
      errorMessages.push('Please select a category');
    }

    const isFoodListingFlow = itemType === 'food';

    // Validate Condition (only for non-food items)
    if (!isFoodListingFlow) {
      if (!formData.condition) {
        errors.condition = true;
        errorMessages.push('Please select the condition of your item');
      }
    }

    // Validate Photos (upload only — no image URL fallback)
    const imageUrlsCheck = uploadedImages.length > 0 ? uploadedImages : [];
    if (imageUrlsCheck.length === 0) {
      errors.photos = true;
      errorMessages.push('Please add at least one photo');
    }

    // Validate Quantity
    if (!formData.quantity || formData.quantity.trim() === '') {
      errors.quantity = true;
      errorMessages.push('Quantity is required');
    } else {
      const quantityValue = parseInt(formData.quantity);
      if (isNaN(quantityValue) || quantityValue < 1) {
        errors.quantity = true;
        errorMessages.push('Quantity must be at least 1');
      }
    }

    // Validate State
    if (!formData.state_id || formData.state_id.trim() === '') {
      errors.state_id = true;
      errorMessages.push('State is required');
    }

    // Validate City
    if (!formData.city_id || formData.city_id.trim() === '') {
      errors.city_id = true;
      errorMessages.push('City is required');
    }

    // Validate Location (regular listings need address or city+state, store listings use store location)
    if (!formData.store_id) {
      const hasAddress = listingLocationAddress?.trim();
      const hasCityState = formData.city_id?.trim() && formData.state_id?.trim();
      if (!hasAddress && !hasCityState) {
        errors.location = true;
        errorMessages.push('Please enter a delivery location address or select city and state');
      }
    }

    // Validate Delivery Time
    if (!formData.delivery_time_option || formData.delivery_time_option.trim() === '') {
      errors.delivery_time_option = true;
      errorMessages.push('Please select a delivery time option');
    }

    // Validate Delivery Fee Type
    if (!formData.delivery_fee_type) {
      errors.delivery_fee_type = true;
      errorMessages.push('Please select a delivery fee type (Fixed Delivery Fee or Distance-Based Deliver)');
    }

    // Individual sellers must set at least one open day
    if (!formData.store_id && !sellerHoursHasAny(sellerHours)) {
      errors.seller_hours = true;
      errorMessages.push('Set at least one open day for your availability hours');
      setSellerHoursError('Set at least one open day for your availability hours');
    } else {
      setSellerHoursError(null);
    }

    // If there are any errors, set field errors and return
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Show comprehensive error message listing all errors
      if (errorMessages.length > 0) {
        const errorText = errorMessages.length === 1 
          ? errorMessages[0] 
          : `Please fix the following errors:\n• ${errorMessages.join('\n• ')}`;
        console.log('Setting error message:', errorText);
        setError(errorText);
        if (errors.seller_hours && sellerHoursRef.current) {
          sellerHoursRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setError('Please fill in all required fields');
      }
      setLoading(false);
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    // Clear any previous errors if validation passes
    setError('');

    // Validate distance-based delivery fields if selected
    if (formData.delivery_fee_type === 'distance') {
      setDeliveryFeeError(''); // Clear previous errors
      
      if (!formData.max_delivery_miles || formData.max_delivery_miles.trim() === '') {
        const errorMsg = 'Please enter the maximum delivery miles for distance-based delivery';
        setError(errorMsg);
        setDeliveryFeeError(errorMsg);
        setLoading(false);
        if (deliveryFeeErrorRef.current) {
          deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      if (!formData.delivery_fee_tiers || formData.delivery_fee_tiers.trim() === '') {
        const errorMsg = 'Please add at least one delivery fee tier for distance-based delivery';
        setError(errorMsg);
        setDeliveryFeeError(errorMsg);
        setLoading(false);
        if (deliveryFeeErrorRef.current) {
          deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      // Validate delivery_fee_tiers is valid JSON
      try {
        const tiers = JSON.parse(formData.delivery_fee_tiers);
        if (!Array.isArray(tiers) || tiers.length === 0) {
          const errorMsg = 'Please add at least one delivery fee tier';
          setError(errorMsg);
          setDeliveryFeeError(errorMsg);
          setLoading(false);
          if (deliveryFeeErrorRef.current) {
            deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
        
        // Validate tier values
        const maxMiles = parseInt(formData.max_delivery_miles) || 0;
        if (maxMiles <= 0) {
          const errorMsg = 'Maximum delivery miles must be greater than 0';
          setError(errorMsg);
          setDeliveryFeeError(errorMsg);
          setLoading(false);
          if (deliveryFeeErrorRef.current) {
            deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
        
        for (let i = 0; i < tiers.length; i++) {
          const tier = tiers[i];
          
          // Convert string values to numbers
          const from = typeof tier.from === 'string' ? (tier.from === '' ? 0 : parseFloat(tier.from) || 0) : tier.from;
          const to = typeof tier.to === 'string' ? (tier.to === '' ? 0 : parseFloat(tier.to) || 0) : tier.to;
          const fee = typeof tier.fee === 'string' ? (tier.fee === '' ? 0 : parseFloat(tier.fee) || 0) : tier.fee;
          
          // Check for empty values
          if (tier.from === '' || tier.to === '' || tier.fee === '') {
            const errorMsg = `Tier ${i + 1}: All fields (From, To, Fee) are required`;
            setError(errorMsg);
            setDeliveryFeeError(errorMsg);
            setLoading(false);
            if (deliveryFeeErrorRef.current) {
              deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
          
          // Check that "to" is greater than "from"
          if (to <= from) {
            const errorMsg = `Tier ${i + 1}: "To" value (${to}) must be greater than "From" value (${from})`;
            setError(errorMsg);
            setDeliveryFeeError(errorMsg);
            setLoading(false);
            if (deliveryFeeErrorRef.current) {
              deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
          
          // Check that "to" doesn't exceed max delivery miles
          if (to > maxMiles) {
            const errorMsg = `Tier ${i + 1}: "To" value (${to} miles) cannot exceed maximum delivery miles (${maxMiles} miles)`;
            setError(errorMsg);
            setDeliveryFeeError(errorMsg);
            setLoading(false);
            if (deliveryFeeErrorRef.current) {
              deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
          
          // Check continuity (each tier should start where previous ends, except first)
          if (i > 0) {
            const prevTier = tiers[i - 1];
            const prevTo = typeof prevTier.to === 'string' ? (prevTier.to === '' ? 0 : parseFloat(prevTier.to) || 0) : prevTier.to;
            if (from < prevTo) {
              const errorMsg = `Tier ${i + 1}: "From" value (${from}) should be at least ${prevTo} (where tier ${i} ends)`;
              setError(errorMsg);
              setDeliveryFeeError(errorMsg);
              setLoading(false);
              if (deliveryFeeErrorRef.current) {
                deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
              return;
            }
          }
          
          // First tier should start at 0
          if (i === 0 && from !== 0) {
            const errorMsg = 'First tier must start at 0 miles';
            setError(errorMsg);
            setDeliveryFeeError(errorMsg);
            setLoading(false);
            if (deliveryFeeErrorRef.current) {
              deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
        }
        
        // Check that last tier's "to" matches max delivery miles
        const lastTier = tiers[tiers.length - 1];
        const lastTo = typeof lastTier.to === 'string' ? (lastTier.to === '' ? 0 : parseFloat(lastTier.to) || 0) : lastTier.to;
        if (lastTo !== maxMiles) {
          const errorMsg = `Last tier's "To" value (${lastTo}) must match maximum delivery miles (${maxMiles})`;
          setError(errorMsg);
          setDeliveryFeeError(errorMsg);
          setLoading(false);
          if (deliveryFeeErrorRef.current) {
            deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
      } catch (e) {
        const errorMsg = 'Invalid delivery fee tiers format. Please check your delivery fee settings';
        setError(errorMsg);
        setDeliveryFeeError(errorMsg);
        setLoading(false);
        if (deliveryFeeErrorRef.current) {
          deliveryFeeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    } else {
      setDeliveryFeeError(''); // Clear errors when not using distance-based delivery
    }

    const imageUrls = imageUrlsCheck;

    // Location: use full address if entered, else fallback to "City, ST" when city/state selected
    const getLocationAddress = () => {
      if (formData.store_id) return undefined;
      const trimmed = listingLocationAddress?.trim();
      if (trimmed) return trimmed;
      const stateId = parseInt(formData.state_id, 10);
      const cityId = parseInt(formData.city_id, 10);
      if (!stateId || !cityId || !Number.isFinite(stateId) || !Number.isFinite(cityId)) return undefined;
      const state = states.find((s) => s.id === stateId);
      const city = cities.find((c) => c.id === cityId);
      if (!state || !city) return undefined;
      return `${city.name}, ${state.abbreviation}`.trim() || undefined;
    };
    const locationAddressToSend = getLocationAddress();
    const cityNameToSend = resolvedCityName?.trim() || (cities.find((c) => c.id === parseInt(formData.city_id, 10))?.name) || undefined;

    // Compute shipping_fee once: fixed = number (including 0), distance = null. Always send so server persists it.
    const isFixedDelivery = formData.delivery_fee_type === 'fixed';
    const rawFee = formData.shipping_fee;
    const shippingFeeNum = isFixedDelivery
      ? (rawFee !== '' && rawFee != null && !Number.isNaN(Number(rawFee)) ? Number(rawFee) : 2.99)
      : null;
    // Send as string so it's never stripped by serialization; server will parse
    const shippingFeePayload = shippingFeeNum != null ? String(shippingFeeNum) : null;
    const maxDeliveryMilesVal = formData.delivery_fee_type === 'distance' && formData.max_delivery_miles?.trim() !== '' ? parseInt(formData.max_delivery_miles, 10) : null;
    const deliveryFeeTiersVal = formData.delivery_fee_type === 'distance' && formData.delivery_fee_tiers?.trim() !== '' ? formData.delivery_fee_tiers : null;

    if (process.env.NODE_ENV === 'development') {
      console.log('CreateListing payload delivery', { delivery_fee_type: formData.delivery_fee_type, shipping_fee: shippingFeePayload, shippingFeeNum });
    }

    try {
      const sellerHoursPayload = !formData.store_id ? sellerHoursPayloadIfAny(sellerHours) : null;
      if (isEditMode && id) {
        // Update existing listing
        const result = await listingsAPI.update(parseInt(id), {
          title: formData.title,
          description: formData.description,
          price: formData.price ? parseFloat(formData.price) : undefined,
          category: normalizeListingCategory(formData.category),
          image_url: imageUrls[0] || undefined,
          image_urls: imageUrls,
          city_id: formData.city_id ? parseInt(formData.city_id, 10) : undefined,
          city_name: cityNameToSend,
          state_id: parseInt(formData.state_id, 10),
          quantity: parseInt(formData.quantity) || 1,
          delivery_time_option: formData.delivery_time_option,
          returns_money_back: formData.returns_money_back,
          store_id: formData.store_id ? parseInt(formData.store_id) : undefined,
          is_shop_store: false,
          shop_store_name: undefined,
          shop_store_address: formData.store_id ? undefined : (locationAddressToSend ?? undefined),
          address: formData.store_id ? undefined : (locationAddressToSend ?? undefined),
          shop_store_close_time: sellerHoursPayload ? sellerHoursToJson(sellerHours) : undefined,
          discount_type: formData.discount_type && formData.discount_value ? (formData.discount_type as 'percentage' | 'fixed') : undefined,
          discount_value: formData.discount_type && formData.discount_value ? parseFloat(formData.discount_value) : undefined,
          discount_min_quantity: formData.discount_type && formData.discount_value ? parseInt(formData.discount_min_quantity) || 2 : undefined,
          shipping_fee: shippingFeePayload,
          max_delivery_miles: maxDeliveryMilesVal,
          free_delivery_miles: undefined, // Removed - no longer used
          delivery_fee_tiers: deliveryFeeTiersVal,
          condition: !isFoodListingFlow ? (formData.condition || 'new') : undefined,
          ...(sellerHoursPayload ?? {}),
        });
        toast.success('Listing updated successfully!');
        setLoading(false);
        const slug = createSlug(result?.title || formData.title || '');
        const path = slug ? `/listings/${id}/${slug}` : `/listings/${id}`;
        window.location.href = path;
      } else {
        // Create new listing
        const result = await listingsAPI.create({
          title: formData.title,
          description: formData.description,
          price: formData.price ? parseFloat(formData.price) : undefined,
          category: normalizeListingCategory(formData.category),
          image_url: imageUrls[0] || undefined,
          image_urls: imageUrls,
          city_id: formData.city_id ? parseInt(formData.city_id, 10) : undefined,
          city_name: cityNameToSend,
          state_id: parseInt(formData.state_id, 10),
          quantity: parseInt(formData.quantity) || 1,
          delivery_time_option: formData.delivery_time_option,
          returns_money_back: formData.returns_money_back,
          store_id: formData.store_id ? parseInt(formData.store_id) : undefined,
          is_shop_store: false,
          shop_store_name: undefined,
          shop_store_address: formData.store_id ? undefined : (locationAddressToSend ?? undefined),
          address: formData.store_id ? undefined : (locationAddressToSend ?? undefined),
          shop_store_close_time: sellerHoursPayload ? sellerHoursToJson(sellerHours) : undefined,
          discount_type: formData.discount_type && formData.discount_value ? (formData.discount_type as 'percentage' | 'fixed') : undefined,
          discount_value: formData.discount_type && formData.discount_value ? parseFloat(formData.discount_value) : undefined,
          discount_min_quantity: formData.discount_type && formData.discount_value ? parseInt(formData.discount_min_quantity) || 2 : undefined,
          shipping_fee: shippingFeePayload,
          max_delivery_miles: maxDeliveryMilesVal,
          free_delivery_miles: undefined, // Removed - no longer used
          delivery_fee_tiers: deliveryFeeTiersVal,
          condition: !isFoodListingFlow ? (formData.condition || 'new') : undefined,
          created_platform: 'web',
          ...(sellerHoursPayload ?? {}),
        });
        
        // Show success message
        toast.success('Listing created successfully!');
        
        // Set loading to false before navigation
        setLoading(false);

        // Home caches the grid for 5 minutes; drop it so the new listing appears.
        clearHomeListingsCache();
        
        // Force navigation immediately
        console.log('Attempting navigation to home page...');
        // Use window.location as primary method since navigate isn't working
        window.location.href = '/';
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} listing:`, err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        formData: {
          title: formData.title,
          category: normalizeListingCategory(formData.category),
          city_id: formData.city_id,
          state_id: formData.state_id,
          itemType,
          payoutInfoComplete
        }
      });
      const status = err.response?.status;
      const is5xx = status && status >= 500;
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      let errorMessage: string;
      if (is5xx || isTimeout) {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else {
        errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || `Failed to ${isEditMode ? 'update' : 'create'} listing`;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const categories = LISTING_CATEGORY_KEYS;

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-2xl font-bold mb-6 text-gray-900">{isEditMode ? 'Edit Listing' : 'Post an Item'}</h1>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <div className="whitespace-pre-line">{error}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div
              className="mb-6 pb-6 border-b border-gray-200"
              role="group"
              aria-label="Listing type"
              data-field="item-type"
            >
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  aria-pressed={itemType === 'other'}
                  aria-label="Items"
                  title="Items"
                  onClick={() => {
                    setItemType('other');
                    setFormData(prev => ({ ...prev, category: 'general' }));
                    if (fieldErrors.category) {
                      setFieldErrors((prev) => {
                        const u = { ...prev };
                        delete u.category;
                        return u;
                      });
                    }
                  }}
                  className={`flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-lg border-2 transition ${
                    itemType === 'other'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <i className="ri-shopping-bag-3-line text-2xl" aria-hidden />
                </button>
                <button
                  type="button"
                  aria-pressed={itemType === 'food'}
                  aria-label="Food"
                  title="Food"
                  onClick={() => {
                    setItemType('food');
                    setFormData(prev => ({ ...prev, category: 'food' }));
                    if (fieldErrors.category) {
                      setFieldErrors((prev) => {
                        const u = { ...prev };
                        delete u.category;
                        return u;
                      });
                    }
                  }}
                  className={`flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-lg border-2 transition ${
                    itemType === 'food'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <i className="ri-restaurant-line text-2xl" aria-hidden />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                data-field="title"
                required
                value={formData.title}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, title: value }));
                  if (fieldErrors.title) {
                    setFieldErrors(prev => {
                      const updated = { ...prev };
                      delete updated.title;
                      return updated;
                    });
                  }
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                  fieldErrors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Enter listing title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                data-field="description"
                value={formData.description}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, description: value }));
                  if (fieldErrors.description) {
                    setFieldErrors(prev => {
                      const updated = { ...prev };
                      delete updated.description;
                      return updated;
                    });
                  }
                }}
                rows={4}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                  fieldErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Describe your item..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price ($)
                </label>
                <input
                  type="number"
                  data-field="price"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, price: value }));
                    if (fieldErrors.price) {
                      setFieldErrors(prev => {
                        const updated = { ...prev };
                        delete updated.price;
                        return updated;
                      });
                    }
                  }}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                    fieldErrors.price ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  data-field="category"
                  key={`category-select-${itemType}-${formData.category}`}
                  required
                  disabled={!itemType}
                  value={!itemType ? '' : formData.category || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      setFormData(prev => ({ ...prev, category: value }));
                      if (fieldErrors.category) {
                        setFieldErrors(prev => { const u = { ...prev }; delete u.category; return u; });
                      }
                    }
                  }}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${
                    fieldErrors.category ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  style={{ WebkitAppearance: 'menulist' }}
                >
                  {!itemType ? (
                    <option value="">Items or food first</option>
                  ) : itemType === 'food' ? (
                    <>
                      <option value="food">Food</option>
                      <option value="shop">Shop</option>
                    </>
                  ) : itemType === 'other' ? (
                    <>
                      <option value="">Select Category</option>
                      {categories.filter(cat => cat !== 'food' && cat !== 'shop').map((cat) => (
                        <option key={cat} value={cat}>
                          {formatListingCategoryLabel(cat)}
                        </option>
                      ))}
                    </>
                  ) : (
                    <>
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {formatListingCategoryLabel(cat)}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {!formData.category && (
                  <p className="text-xs text-red-500 mt-1">Please select a category</p>
                )}
              </div>
            </div>

            {itemType === 'other' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  data-field="condition"
                  value={formData.condition}
                  onChange={(e) => {
                    const value = e.target.value as 'new' | 'new_open_box' | 'seller_refurbished' | 'used' | 'for_parts';
                    setFormData(prev => ({ ...prev, condition: value }));
                    if (fieldErrors.condition) {
                      setFieldErrors(prev => {
                        const updated = { ...prev };
                        delete updated.condition;
                        return updated;
                      });
                    }
                  }}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                    fieldErrors.condition ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                >
                  <option value="new">New</option>
                  <option value="new_open_box">New – Open box</option>
                  <option value="seller_refurbished">Seller refurbished</option>
                  <option value="used">Used</option>
                  <option value="for_parts">For parts or not working</option>
                </select>
              </div>
            )}

            {/* Multiple Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photos ({uploadedImages.length}/10)
              </label>
              <div className="space-y-3">
                {/* Uploaded Images Grid */}
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {uploadedImages.map((imageUrl, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={imageUrl}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                {uploadedImages.length < 10 && (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      disabled={uploadingImages}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      data-field="photos"
                      className={`flex items-center justify-center px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition ${
                        uploadingImages ? 'opacity-50 cursor-not-allowed' : ''
                      } ${
                        fieldErrors.photos ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    >
                      {uploadingImages ? (
                        <span className="text-gray-600">Uploading...</span>
                      ) : (
                        <>
                          <i className="ri-image-add-line text-xl mr-2 text-gray-600"></i>
                          <span className="text-gray-700 font-medium">
                            {uploadedImages.length === 0 ? 'Add Photos' : 'Add More Photos'}
                          </span>
                        </>
                      )}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Upload up to 10 images. JPG, PNG, GIF, or WebP.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity *
              </label>
              <input
                type="number"
                min="1"
                required
                data-field="quantity"
                value={formData.quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, quantity: value }));
                  if (fieldErrors.quantity) {
                    setFieldErrors(prev => {
                      const updated = { ...prev };
                      delete updated.quantity;
                      return updated;
                    });
                  }
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                  fieldErrors.quantity ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="1"
              />
              <p className="text-xs text-gray-500 mt-1">Number of items available</p>
            </div>

            <div data-field="location">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <div className={fieldErrors.location ? 'border-2 border-red-500 rounded-lg p-1 bg-red-50' : ''}>
                <LocationAutocomplete
                  value={listingLocationAddress}
                  onInputChange={(v) => setListingLocationAddress(v)}
                  onPlaceSelect={async (place) => {
                    if (fieldErrors.location) {
                      setFieldErrors(prev => {
                        const updated = { ...prev };
                        delete updated.location;
                        return updated;
                      });
                    }
                  try {
                    console.log('Location selected:', place);
                    setListingLocationAddress(place.address);
                    setResolvedCityName(place.city || '');
                    
                    // Geocode the address for map display
                    await geocodeAddress(place.address);
                    
                    // Find matching state and city IDs
                    const { stateId, cityId } = await findLocationIds(
                      place.city,
                      place.stateAbbreviation
                    );
                    
                    console.log('Location IDs found:', { stateId, cityId, cityName: place.city });
                    
                    if (stateId != null && stateId !== undefined) {
                      // If city doesn't exist yet, add it to the database
                      let finalCityId = cityId;
                      if ((finalCityId == null || finalCityId === undefined) && place.city) {
                        try {
                          const newCity = await locationsAPI.addCity(place.city, stateId);
                          if (newCity && newCity.id) {
                            finalCityId = newCity.id;
                            // Reload cities for this state so the new city appears in the list
                            const updatedCities = await locationsAPI.getCitiesByState(stateId);
                            setCities(updatedCities);
                          }
                        } catch (addErr) {
                          console.error('Failed to add city to database:', addErr);
                        }
                      }
                      // Store both cityId and cityName (cityName as backup)
                      if (finalCityId != null && finalCityId !== undefined) {
                        pendingCityIdRef.current = finalCityId;
                      }
                      if (place.city) {
                        // Always store city name as backup, even if we have ID
                        pendingCityNameRef.current = place.city;
                      }
                      
                      // Update state_id - this will trigger useEffect to load cities
                      const stateIdStr = String(stateId);
                      setFormData(prev => ({
                        ...prev,
                        state_id: stateIdStr,
                        city_id: finalCityId ? String(finalCityId) : '' // If we created/found the city, set it immediately
                      }));
                    }
                  } catch (error) {
                    console.error('Error updating location:', error);
                    pendingCityIdRef.current = null;
                    pendingCityNameRef.current = null;
                  }
                }}
                placeholder="Enter your location (address, city, state)"
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                  fieldErrors.location ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
              </div>
              <p className="text-xs text-gray-500 mt-1">Start typing your address and select from suggestions. City and state are set from your selection.</p>
            </div>

            {/* Max Delivery Distance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Maximum Delivery Distance
              </label>
              <div className="relative px-2">
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={formData.max_delivery_miles || '25'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, max_delivery_miles: value }));
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #10b981 0%, #10b981 ${((parseInt(formData.max_delivery_miles || '25') - 5) / 95) * 100}%, #e5e7eb ${((parseInt(formData.max_delivery_miles || '25') - 5) / 95) * 100}%, #e5e7eb 100%)`
                  }}
                />
                <style>{`
                  input[type="range"]::-webkit-slider-thumb {
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: white;
                    border: 3px solid #10b981;
                    cursor: pointer;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  }
                  input[type="range"]::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: white;
                    border: 3px solid #10b981;
                    cursor: pointer;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  }
                `}</style>
                <div className="flex justify-between mt-2 mb-3">
                  <span className="text-xs text-gray-500">5 miles</span>
                  <span className="text-xs text-gray-500">100 miles</span>
                </div>
                <div className="text-center">
                  <div className="inline-block px-3 py-1 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <span className="text-base font-semibold text-gray-900">
                      {formData.max_delivery_miles || '25'} miles
                    </span>
                    {parseInt(formData.max_delivery_miles || '25') === 100 && (
                      <span className="ml-2 text-xs text-gray-500 font-normal">(Maximum)</span>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Maximum distance you're willing to travel for delivery</p>
            </div>

            {/* Map Preview */}
            <div>
              {mapLoading && (
                <div className="mt-4 h-64 rounded-lg border border-gray-300 flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-2"></div>
                    <p className="text-sm text-gray-600">Loading map...</p>
                  </div>
                </div>
              )}
              {!mapLoading && mapCoords && (
                <div className="mt-4 h-64 rounded-lg overflow-hidden border border-gray-300">
                  <MapboxMapWrapper
                    center={mapCoords}
                    zoom={15}
                    markers={[{
                      position: mapCoords,
                      content: (
                        <div className="w-6 h-6 bg-green-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                          <i className="ri-map-pin-fill text-white text-xs"></i>
                        </div>
                      ),
                      popup: (
                        <div className="text-sm font-medium">Your Location</div>
                      )
                    }]}
                    scrollZoom={true}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Time *
              </label>
              <select
                data-field="delivery_time_option"
                required
                value={formData.delivery_time_option}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, delivery_time_option: value }));
                  if (fieldErrors.delivery_time_option) {
                    setFieldErrors(prev => { const u = { ...prev }; delete u.delivery_time_option; return u; });
                  }
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                  fieldErrors.delivery_time_option ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              >
                <option value="1-2">⚡ Fast: 1-2 hours</option>
                <option value="3-6">🚚 Standard: 3-6 hours</option>
                <option value="24">📦 Next Day: 24 hours</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Choose your delivery speed</p>
            </div>

            <div>
              <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={formData.returns_money_back}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({ ...prev, returns_money_back: checked }));
                  }}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <div>
                  <span className="font-semibold text-gray-900">↩️ Returns, Money Back</span>
                  <p className="text-xs text-gray-500 mt-1">Offer returns and money back guarantee for this item</p>
                </div>
              </label>
            </div>

            {/* Discount Section */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">💰 Bulk Discount (Optional)</h3>
              <p className="text-sm text-gray-600 mb-4">Offer a discount when customers buy multiple quantities</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Type
                  </label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => {
                      const value = e.target.value as 'percentage' | 'fixed' | '';
                      setFormData(prev => ({ 
                        ...prev, 
                        discount_type: value,
                        discount_value: value ? prev.discount_value : '',
                        discount_min_quantity: value ? prev.discount_min_quantity : '2'
                      }));
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                  >
                    <option value="">No discount</option>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>

                {formData.discount_type && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Value {formData.discount_type === 'percentage' ? '(%)' : '($)'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={formData.discount_type === 'percentage' ? '100' : undefined}
                        step={formData.discount_type === 'percentage' ? '0.1' : '0.01'}
                        value={formData.discount_value}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData(prev => ({ ...prev, discount_value: value }));
                        }}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                        placeholder={formData.discount_type === 'percentage' ? '10' : '5.00'}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.discount_type === 'percentage' 
                          ? 'Percentage discount (e.g., 10 for 10% off)' 
                          : 'Fixed dollar amount discount (e.g., 5.00 for $5 off)'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Quantity for Discount
                      </label>
                      <input
                        type="number"
                        min="2"
                        value={formData.discount_min_quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData(prev => ({ ...prev, discount_min_quantity: value }));
                        }}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                        placeholder="2"
                      />
                      <p className="text-xs text-gray-500 mt-1">Discount applies when buying this many or more items</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Delivery Fee Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Fee Type <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                  formData.delivery_fee_type === 'fixed' 
                    ? 'border-green-500 bg-green-50' 
                    : fieldErrors.delivery_fee_type
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}>
                  <input
                    type="radio"
                    name="delivery_fee_type"
                    value="fixed"
                    checked={formData.delivery_fee_type === 'fixed'}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, delivery_fee_type: 'fixed' }));
                      setDeliveryFeeError(''); // Clear error when switching to fixed
                    }}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Fixed Delivery Fee</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Set a fixed delivery fee for all orders
                    </div>
                  </div>
                </label>
                
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                  formData.delivery_fee_type === 'distance' 
                    ? 'border-green-500 bg-green-50' 
                    : fieldErrors.delivery_fee_type
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}>
                  <input
                    type="radio"
                    name="delivery_fee_type"
                    value="distance"
                    data-field="delivery_fee_type"
                    checked={formData.delivery_fee_type === 'distance'}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, delivery_fee_type: 'distance' }));
                      setDeliveryFeeError(''); // Clear error when switching to distance-based
                      if (fieldErrors.delivery_fee_type) {
                        setFieldErrors(prev => {
                          const updated = { ...prev };
                          delete updated.delivery_fee_type;
                          return updated;
                        });
                      }
                    }}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Distance-Based Deliver</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Manually set delivery fee based on distance
                    </div>
                  </div>
                </label>
              </div>
              
              {/* Distance-Based Options - only show when distance-based is selected */}
              {formData.delivery_fee_type === 'distance' && (
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Delivery Miles <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={(() => {
                          const miles = formData.max_delivery_miles;
                          if (!miles || miles === '') return 'custom';
                          // If it's a preset value and custom mode is NOT active, show the preset
                          if (!showCustomMaxMiles && ['5', '10', '15', '20', '25', '30', '40', '50'].includes(miles)) {
                            return miles;
                          }
                          // Otherwise show 'custom'
                          return 'custom';
                        })()}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === 'custom') {
                            setShowCustomMaxMiles(true);
                            // Keep current value if it exists, otherwise use last tier's value
                            setFormData(prev => {
                              const currentMiles = prev.max_delivery_miles;
                              if (currentMiles && !['5', '10', '15', '20', '25', '30', '40', '50'].includes(currentMiles)) {
                                return prev;
                              }
                              // Try to get last tier's value
                              try {
                                const tiers = JSON.parse(prev.delivery_fee_tiers || '[]');
                                if (Array.isArray(tiers) && tiers.length > 0) {
                                  const lastTier = tiers[tiers.length - 1];
                                  if (lastTier && lastTier.to) {
                                    return { ...prev, max_delivery_miles: lastTier.to.toString() };
                                  }
                                }
                              } catch {}
                              return prev;
                            });
                          } else {
                            setShowCustomMaxMiles(false);
                            const presetValue = value;
                            setFormData(prev => {
                              // Update max_delivery_miles and last tier
                              let updatedTiers = prev.delivery_fee_tiers;
                              if (presetValue) {
                                try {
                                  const tiers = JSON.parse(prev.delivery_fee_tiers || '[]');
                                  if (Array.isArray(tiers) && tiers.length > 0) {
                                    const maxMiles = parseInt(presetValue);
                                    if (!isNaN(maxMiles)) {
                                      const updated = [...tiers];
                                      const lastTier = updated[updated.length - 1];
                                      if (lastTier) {
                                        updated[updated.length - 1] = { ...lastTier, to: maxMiles };
                                        updatedTiers = JSON.stringify(updated);
                                      }
                                    }
                                  }
                                } catch {}
                              }
                              return { 
                                ...prev, 
                                max_delivery_miles: presetValue,
                                delivery_fee_tiers: updatedTiers
                              };
                            });
                          }
                        }}
                        className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                        required={formData.delivery_fee_type === 'distance'}
                      >
                        <option value="custom">Custom</option>
                        <option value="5">5 miles</option>
                        <option value="10">10 miles</option>
                        <option value="15">15 miles</option>
                        <option value="20">20 miles</option>
                        <option value="25">25 miles</option>
                        <option value="30">30 miles</option>
                        <option value="40">40 miles</option>
                        <option value="50">50 miles</option>
                      </select>
                      {showCustomMaxMiles && (
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={formData.max_delivery_miles || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(prev => {
                              let updatedTiers = prev.delivery_fee_tiers;
                              if (value) {
                                try {
                                  const tiers = JSON.parse(prev.delivery_fee_tiers || '[]');
                                  if (Array.isArray(tiers) && tiers.length > 0) {
                                    const maxMiles = parseInt(value);
                                    if (!isNaN(maxMiles)) {
                                      const updated = [...tiers];
                                      const lastTier = updated[updated.length - 1];
                                      if (lastTier) {
                                        updated[updated.length - 1] = { ...lastTier, to: maxMiles };
                                        updatedTiers = JSON.stringify(updated);
                                      }
                                    }
                                  }
                                } catch {}
                              }
                              return { 
                                ...prev, 
                                max_delivery_miles: value,
                                delivery_fee_tiers: updatedTiers
                              };
                            });
                          }}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                          placeholder="Enter miles"
                        />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Last tier fee automatically applies to all distances up to max miles.</p>
                  </div>
                  
                  <div ref={deliveryFeeErrorRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Distance-Based Fee Tiers
                    </label>
                    <DeliveryFeeTiersEditor
                      tiers={formData.delivery_fee_tiers}
                      error={deliveryFeeError}
                      onChange={(tiers) => {
                        setDeliveryFeeError(''); // Clear error when user makes changes
                        setFormData(prev => {
                          // Only update max_delivery_miles if we're in custom mode
                          // This prevents overriding preset selections
                          if (!showCustomMaxMiles) {
                            // User selected a preset, don't override it - just update tiers
                            return { ...prev, delivery_fee_tiers: tiers };
                          }
                          
                          try {
                            const parsedTiers = JSON.parse(tiers);
                            if (Array.isArray(parsedTiers) && parsedTiers.length > 0) {
                              const lastTier = parsedTiers[parsedTiers.length - 1];
                              if (lastTier && lastTier.to) {
                                const lastTierValue = lastTier.to.toString();
                                // Only update if we're in custom mode
                                // If it's not a preset, ensure custom mode is on
                                if (!['5', '10', '15', '20', '25', '30', '40', '50'].includes(lastTierValue)) {
                                  setShowCustomMaxMiles(true);
                                }
                                return { ...prev, delivery_fee_tiers: tiers, max_delivery_miles: lastTierValue };
                              }
                            }
                          } catch {}
                          return { ...prev, delivery_fee_tiers: tiers };
                        });
                      }}
                      freeMiles={0}
                      maxMiles={parseInt(formData.max_delivery_miles) || 25}
                    />
                  </div>
                </div>
              )}
              
              {/* Fixed Fee Input - only show when fixed is selected */}
              {formData.delivery_fee_type === 'fixed' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Fee ($) <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.shipping_fee}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, shipping_fee: value }));
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                      placeholder="2.99"
                      required={formData.delivery_fee_type === 'fixed'}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Standard delivery fee is $2.99. You can set a custom fee. Fixed fee covers all delivery distances in region.</p>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              {stores.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Store (Optional)
                  </label>
                  <select
                    value={formData.store_id}
                    onChange={(e) => {
                      const storeId = e.target.value;
                      setFormData(prev => ({ 
                        ...prev, 
                        store_id: storeId,
                        is_shop_store: false,
                        shop_store_name: '',
                        shop_store_address: '',
                        shop_store_close_time: ''
                      }));
                      if (storeId) {
                        setSellerHours(defaultSellerHours());
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                  >
                    <option value="">No store (individual listing)</option>
                    {stores.map((store) => (
                      <option key={store.id} value={store.id}>
                        <i className="ri-store-line mr-1"></i> {store.name} - {store.city_name}, {store.state_abbreviation}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Link this item to one of your stores, or <Link to="/my-stores" className="text-green-600 hover:underline">create a new store</Link>
                  </p>
                </div>
              )}
              
              {!formData.store_id && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 mb-2">
                    <i className="ri-store-line mr-1"></i>
                    Want to create a shop/store listing?
                  </p>
                  <Link 
                    to="/my-stores" 
                    className="text-green-600 hover:text-green-700 hover:underline font-medium text-sm inline-flex items-center"
                  >
                    Create a store first →
                  </Link>
                </div>
              )}

              {!formData.store_id && (
                <div className="mb-4" key="seller-hours-editor" ref={sellerHoursRef}>
                  <SellerHoursEditor
                    key={isEditMode ? id : 'create'}
                    hours={sellerHours}
                    onChange={(hours) => {
                      setSellerHours(hours);
                      if (sellerHoursHasAny(hours)) setSellerHoursError(null);
                    }}
                    error={sellerHoursError}
                  />
                </div>
              )}
            </div>

            {/* Show requirements warning for new listings */}
            {!isEditMode && !payoutInfoComplete && !checkingRequirements && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium mb-2">
                  Complete your account setup to post listings:
                </p>
                <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                  {!payoutInfoComplete && (
                    <li>Payout information (Stripe Connect account setup)</li>
                  )}
                </ul>
                <Link
                  to="/payout-settings"
                  className="mt-3 inline-block text-sm text-yellow-800 underline hover:text-yellow-900"
                >
                  Go to Payout Settings to complete →
                </Link>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading || checkingRequirements || (!isEditMode && !payoutInfoComplete)}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={(e) => {
                  // Debug logging
                  if (loading || checkingRequirements || (!isEditMode && !payoutInfoComplete)) {
                    console.log('Button disabled:', {
                      loading,
                      checkingRequirements,
                      isEditMode,
                      payoutInfoComplete
                    });
                  } else {
                    console.log('Button enabled - form data:', {
                      title: formData.title,
                      category: normalizeListingCategory(formData.category),
                      city_id: formData.city_id,
                      state_id: formData.state_id,
                      itemType
                    });
                  }
                }}
              >
                {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Listing' : 'Create listing')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-4 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateListing;

